import DeleteDialog from "@/components/DeleteDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { setFormErrors } from "@/lib/react-form-utils";
import { CodeService, type Code } from "@/services/CodeService";
import { SpeciesService, type Species } from "@/services/SpeciesService";
import { ValidationError } from "@/types/problemDetails";
import axios from "axios";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

interface SpeciesRegisterProps {
  siteId: string;
  tenantId: string;
}

type SpeciesFormValues = {
  name: string;
  scientificName: string;
  majorCategory: string;
  prioritySpecies: boolean;
  flagshipSpecies: boolean;
  active: boolean;
  notes: string;
};

const emptyFormValues = (majorCategory: string): SpeciesFormValues => ({
  name: "",
  scientificName: "",
  majorCategory,
  prioritySpecies: false,
  flagshipSpecies: false,
  active: true,
  notes: "",
});

const toFormValues = (species: Species): SpeciesFormValues => ({
  name: species.name,
  scientificName: species.scientificName,
  majorCategory: species.majorCategory,
  prioritySpecies: species.prioritySpecies,
  flagshipSpecies: species.flagshipSpecies,
  active: species.active,
  notes: species.notes ?? "",
});

const SpeciesRegister: React.FC<SpeciesRegisterProps> = ({ siteId, tenantId }) => {
  const [speciesList, setSpeciesList] = useState<Species[]>([]);
  const [categories, setCategories] = useState<Code[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSpecies, setEditingSpecies] = useState<Species | null>(null);
  const [deletedSpeciesId, setDeletedSpeciesId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setError,
  } = useForm<SpeciesFormValues>();

  const defaultCategoryId = useMemo(
    () => categories.find((category) => category.isDefault)?.id ?? categories[0]?.id ?? "",
    [categories]
  );

  const categoryName = (categoryId: string) =>
    categories.find((category) => category.id === categoryId)?.name ?? "Unknown";

  const loadRegister = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [loadedSpecies, codes] = await Promise.all([
        SpeciesService.getBySiteId(siteId),
        CodeService.getCodes2(),
      ]);
      const loadedCategories = codes.filter((code) => code.type === "SpeciesMajorCategory");
      setSpeciesList([...loadedSpecies].sort((a, b) => a.name.localeCompare(b.name)));
      setCategories([...loadedCategories].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Failed to load species register:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    void loadRegister();
  }, [loadRegister]);

  const openCreateForm = () => {
    setEditingSpecies(null);
    reset(emptyFormValues(defaultCategoryId));
    setIsFormOpen(true);
  };

  const openEditForm = (species: Species) => {
    setEditingSpecies(species);
    reset(toFormValues(species));
    setIsFormOpen(true);
  };

  const applyServerErrors = (error: unknown, data: SpeciesFormValues) => {
    if (error instanceof ValidationError) {
      setFormErrors<SpeciesFormValues>(error.errors, setError, data);
      return true;
    }
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      const apiErrors = error.response.data?.errors as Record<string, string | string[]> | undefined;
      if (apiErrors) {
        setFormErrors<SpeciesFormValues>(apiErrors, setError, data);
        return true;
      }
    }
    return false;
  };

  const onSubmit = async (data: SpeciesFormValues) => {
    const payload: Omit<Species, "id"> = {
      name: data.name,
      scientificName: data.scientificName,
      majorCategory: data.majorCategory,
      prioritySpecies: data.prioritySpecies,
      flagshipSpecies: data.flagshipSpecies,
      active: data.active,
      notes: data.notes?.trim() ? data.notes : null,
      siteId,
      tenantId,
    };

    try {
      if (editingSpecies) {
        const updatedSpecies: Species = { ...payload, id: editingSpecies.id };
        await SpeciesService.update(editingSpecies.id, updatedSpecies);
        setSpeciesList((current) =>
          current
            .map((item) => (item.id === updatedSpecies.id ? updatedSpecies : item))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } else {
        const created = await SpeciesService.create(payload);
        setSpeciesList((current) =>
          [...current, created].sort((a, b) => a.name.localeCompare(b.name))
        );
      }
      setIsFormOpen(false);
      setEditingSpecies(null);
    } catch (error) {
      if (!applyServerErrors(error, data)) {
        setError("root", {
          type: "server",
          message: "Failed to save species. Please try again.",
        });
      }
    }
  };

  return (
    <section className="mt-10 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Species register</h2>
        <Button type="button" onClick={openCreateForm} disabled={loading || loadError || categories.length === 0} className="bg-blue-600 text-white hover:bg-blue-700">
          <Plus className="mr-1 h-4 w-4" />
          Add species
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading species...</p>
      ) : loadError ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-red-600">Failed to load the species register.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void loadRegister()}>
            Retry
          </Button>
        </div>
      ) : speciesList.length === 0 ? (
        <p className="text-sm text-gray-500">No species recorded for this site yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
            <thead>
              <tr className="bg-gray-50">
                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">Name</th>
                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">Scientific name</th>
                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">Major category</th>
                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">Flags</th>
                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {speciesList.map((species) => (
                <tr key={species.id} className="border-t border-gray-100">
                  <td className="py-2 px-4 text-sm text-gray-700">{species.name}</td>
                  <td className="py-2 px-4 text-sm text-gray-600 italic">{species.scientificName}</td>
                  <td className="py-2 px-4 text-sm text-gray-600">{categoryName(species.majorCategory)}</td>
                  <td className="py-2 px-4 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {species.prioritySpecies && <Badge variant="secondary">Priority</Badge>}
                      {species.flagshipSpecies && <Badge variant="secondary">Flagship</Badge>}
                    </div>
                  </td>
                  <td className="py-2 px-4 text-sm">
                    <Badge variant={species.active ? "default" : "outline"}>
                      {species.active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="py-2 px-4 text-sm">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="hover:text-blue-600"
                        onClick={() => openEditForm(species)}
                        aria-label={`Edit ${species.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="hover:text-red-600"
                        onClick={() => {
                          setDeletedSpeciesId(species.id);
                          setShowDeleteDialog(true);
                        }}
                        aria-label={`Delete ${species.name}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSpecies ? "Edit species" : "Add species"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input id="name" type="text" {...register("name", { required: "Name is required" })} />
              <span className="text-sm text-red-500">{errors.name?.message}</span>
            </div>
            <div>
              <Label htmlFor="scientificName">Scientific name *</Label>
              <Input
                id="scientificName"
                type="text"
                placeholder="e.g. Alauda arvensis"
                {...register("scientificName", { required: "Scientific name is required" })}
              />
              <span className="text-sm text-red-500">{errors.scientificName?.message}</span>
            </div>
            <div>
              <Label htmlFor="majorCategory">Major category *</Label>
              <select
                id="majorCategory"
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                {...register("majorCategory", { required: "Major category is required" })}
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <span className="text-sm text-red-500">{errors.majorCategory?.message}</span>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register("prioritySpecies")} />
                Priority species
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register("flagshipSpecies")} />
                Flagship species
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register("active")} />
                Active
              </label>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" rows={3} {...register("notes")} />
              <span className="text-sm text-red-500">{errors.notes?.message}</span>
            </div>
            {errors.root?.message && (
              <p className="text-sm text-red-500">{errors.root.message}</p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {editingSpecies ? "Update species" : "Create species"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteDialog
        title="Delete species"
        onDelete={async () => {
          if (!deletedSpeciesId) {
            return;
          }
          await SpeciesService.delete(deletedSpeciesId);
          setSpeciesList((current) => current.filter((item) => item.id !== deletedSpeciesId));
          setDeletedSpeciesId(null);
        }}
        deleteConfirmationText={<p>Are you sure you want to delete this species?</p>}
        isOpen={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
      />
    </section>
  );
};

export default SpeciesRegister;
